/**
 * Cases closed as suspicious: the decision within 24 hours (LFPIORPI art. 18 fr. VI, S-32).
 *
 * An alert confirmed as suspicious, or a sanctions match confirmed, starts a clock. Here the designated
 * compliance officer (or a super_admin) records that an aviso was filed, with the reference the SAT
 * portal gave it, or that none was filed, with the reason. A decision not to file is never the one of the
 * person who closed the case. Nothing of this reaches the client or the Account Executive (art. 38).
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import suspicionAvisosService, {
  type AvisoDecision,
  type SuspicionClock,
} from '../../services/suspicionAvisos.service';
import { cleanMfaInput, stepUpErrorMessage } from '../../lib/mfaApproval';
import {
  canSend,
  dueAt,
  hoursLeft,
  isOverdue,
  mayDecideNotFiled,
  sourceLabel,
} from '../../lib/suspicionAvisos';
import { formatDateTime } from '../../lib/datetime';
import authService from '../../services/auth.service';
import { colors } from '../../styles/theme';

function DecisionForm({
  clock,
  myId,
  onDone,
}: Readonly<{ clock: SuspicionClock; myId: string | null; onDone: () => void }>): JSX.Element {
  // No decision is chosen for the officer: each one is a regulated act.
  const [decision, setDecision] = useState<AvisoDecision | null>(null);
  const notFiledAllowed = mayDecideNotFiled(clock, myId);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (decision === null) return;
      await suspicionAvisosService.decide(clock.id, {
        decision,
        aviso_reference: decision === 'filed' ? reference.trim() : null,
        reason: decision === 'not_filed' ? reason.trim() : null,
        mfa_code: code.trim(),
      });
      onDone();
    } catch (e: unknown) {
      setError(stepUpErrorMessage(e, 'Could not record the decision.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4">
      <div className="flex gap-4 text-sm" role="radiogroup" aria-label="Decision">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name={`decision-${clock.id}`}
            checked={decision === 'filed'}
            onChange={() => setDecision('filed')}
          />
          {' '}
          An aviso was filed
        </label>
        <label className="flex items-center gap-2" style={{ opacity: notFiledAllowed ? 1 : 0.5 }}>
          <input
            type="radio"
            name={`decision-${clock.id}`}
            checked={decision === 'not_filed'}
            disabled={!notFiledAllowed}
            onChange={() => setDecision('not_filed')}
          />
          {' '}
          No aviso was filed
        </label>
      </div>
      {!notFiledAllowed && (
        <p className="text-xs mt-2" style={{ color: colors.muted }}>
          You closed this case: a decision that no aviso is filed is recorded by another officer.
        </p>
      )}
      {decision === 'filed' && (
        <>
          <label className="block text-xs mt-3 mb-1" htmlFor={`ref-${clock.id}`} style={{ color: colors.muted }}>
            Reference of the aviso
          </label>
          <input
            id={`ref-${clock.id}`}
            type="text"
            value={reference}
            maxLength={120}
            onChange={(e) => setReference(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}` }}
          />
        </>
      )}
      {decision === 'not_filed' && (
        <>
          <label className="block text-xs mt-3 mb-1" htmlFor={`reason-${clock.id}`} style={{ color: colors.muted }}>
            The reason for the decision
          </label>
          <textarea
            id={`reason-${clock.id}`}
            value={reason}
            rows={2}
            maxLength={1000}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}` }}
          />
        </>
      )}
      <div className="flex flex-wrap items-end gap-3 mt-3">
        <div>
          <label className="block text-xs mb-1" htmlFor={`code-${clock.id}`} style={{ color: colors.muted }}>
            Authentication code
          </label>
          <input
            id={`code-${clock.id}`}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(cleanMfaInput(e.target.value))}
            className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36"
            style={{ border: `1px solid ${colors.border}` }}
          />
        </div>
        <button
          type="button"
          disabled={busy || !canSend({ decision, reference, reason, code })}
          onClick={() => { void send(); }}
          className="text-sm px-3 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: colors.gold, color: colors.navy }}
        >
          Record the decision
        </button>
      </div>
      {error && <p className="text-sm mt-3" role="alert" style={{ color: colors.danger }}>{error}</p>}
    </div>
  );
}

function ClockCard({
  clock,
  myId,
  onDone,
}: Readonly<{ clock: SuspicionClock; myId: string | null; onDone: () => void }>): JSX.Element {
  const decided = clock.decision !== null;
  return (
    <li
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>{sourceLabel(clock.source_kind)}</p>
          <p className="text-xs" style={{ color: colors.muted }}>
            <span translate="no">{clock.client_name ?? '—'}</span> · closed {formatDateTime(clock.started_at)}
            {clock.started_by_name && (
              <> by <span translate="no">{clock.started_by_name}</span></>
            )}
          </p>
          {!decided && (
            <p className="text-xs font-semibold" style={{ color: isOverdue(clock) ? colors.danger : colors.inkSoft }}>
              Decision due {formatDateTime(dueAt(clock))}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {isOverdue(clock) && <StatusBadge tone="danger">Decision overdue</StatusBadge>}
          {!decided && !isOverdue(clock) && (
            <StatusBadge tone="warning">{hoursLeft(clock)} hours left</StatusBadge>
          )}
          {decided && (
            <StatusBadge tone="neutral">
              {clock.decision === 'filed' ? 'Aviso filed' : 'No aviso filed'}
            </StatusBadge>
          )}
        </div>
      </div>
      {decided ? (
        <>
          <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: colors.inkSoft }}>
            {clock.decision === 'filed' ? clock.aviso_reference : clock.reason}
          </p>
          {clock.decided_at && (
            <p className="text-xs mt-1" style={{ color: colors.muted }}>
              Recorded {formatDateTime(clock.decided_at)}
              {clock.decided_by_name && (
                <> by <span translate="no">{clock.decided_by_name}</span></>
              )}
            </p>
          )}
        </>
      ) : (
        <DecisionForm clock={clock} myId={myId} onDone={onDone} />
      )}
    </li>
  );
}

export default function SuspicionAvisos(): JSX.Element {
  const [rows, setRows] = useState<SuspicionClock[]>([]);
  const [all, setAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [myId, setMyId] = useState<string | null>(null);

  useEffect(() => {
    authService.getMyId().then(setMyId).catch(() => setMyId(null));
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await suspicionAvisosService.list(!all));
    } catch {
      setError('Only the compliance officer and super admins can open this page.');
    } finally {
      setLoading(false);
    }
  }, [all]);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Suspicion reports"
        subtitle="Cases closed as suspicious. Within 24 hours, record whether an aviso was filed."
        actions={
          <label className="flex items-center gap-2 text-sm" style={{ color: colors.inkSoft }}>
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
            {' '}
            Show decided cases too
          </label>
        }
      />
      {error && <p className="mb-4 text-sm" role="alert" style={{ color: colors.danger }}>{error}</p>}
      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {!loading && !error && rows.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>Nothing is waiting.</p>
      )}
      <ul className="space-y-3">
        {rows.map((c) => <ClockCard key={c.id} clock={c} myId={myId} onDone={() => { void reload(); }} />)}
      </ul>
    </div>
  );
}
